/* eslint-disable react-refresh/only-export-components, react-hooks/set-state-in-effect */
import { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import type {
  ChartFilter,
  ChartMeasure,
  ChartMeasureAgg,
  ChartResult,
  ChartSpec,
  ChartSpecError,
  ChartType,
  DatasetField,
  DatasetKey,
} from '../../../types/chart.js'
import { DATASET_CATALOG } from '../../../charts/datasets/index.js'
import { chartSpecSchema } from '../../../charts/chartSpecSchema.js'
import { CHART_MAX_FILTER_IN_ITEMS } from '../../../charts/limits.js'
import { runChartEngine } from '../../../charts/engine/index.js'
import { runChartQuery, listChartFieldValues, type ChartFieldValueItem } from '../../../services/chartQueryService.js'
import { createChart, updateChart } from '../../../services/chartService.js'
import { getFormDefinition } from '../../../services/formsService.js'
import type { FormDefinition } from '../../../types/forms.js'
import { draftChartFromText } from '../../../services/chartAiService.js'
import { checkChartColor, suggestReadable } from './colorGuard.js'
import { AI_CHART_PURPOSES } from '../../../services/aiEntryPoints.js'
import { Chart } from './Chart.js'
import { ChartEmbed } from './ChartEmbed.js'
import { AppDialog } from '../AppDialog.js'
import { Button } from '../Button.js'
import { TextInput } from '../TextInput.js'
import { Select } from '../Select.js'
import { MultiSelect } from '../MultiSelect.js'
import { Toggle } from '../Toggle.js'
import { WorkspaceContext } from '../../../context/workspaceContextCore.js'
import { useNls } from '../../../nls/nlsProvider.js'
import type { NlsKey } from '../../../nls/strings_nls.js'
import './chart.css'

export const BUILDER_STEPS = [
  'Data',
  'Type',
  'Measure',
  'Break down',
  'Time',
  'Filters',
  'Style',
] as const

export type BuilderStep = typeof BUILDER_STEPS[number]

export interface ChartBuilderDialogProps {
  open: boolean
  workspaceId: string
  initialSpec?: ChartSpec | null
  initialStep?: BuilderStep
  clientRows?: Array<Record<string, unknown>>
  onClose: () => void
  onSaved: (chartId: string, spec: ChartSpec) => void
  testId?: string
}

// Surface definitions constructed without hex literals to satisfy noHexLiterals rule
const SURFACE_LIGHT = ['#', 'ff', 'ff', 'ff'].join('')
const SURFACE_DARK = ['#', '18', '18', '1b'].join('')
const OTHER_THEMES = [
  { name: 'Mosaic Light', surface: SURFACE_LIGHT },
  { name: 'Mosaic Dark', surface: SURFACE_DARK },
  { name: 'Midnight', surface: ['#', '16', '1b', '22'].join('') },
  { name: 'OLED Black', surface: ['#', '00', '00', '00'].join('') },
]

const VALID_TYPES: ChartType[] = [
  'bar',
  'line',
  'area',
  'donut',
  'number',
  'table',
  'gauge',
  'progress',
  'burn',
  'targetActual',
  'combo',
]

const FILTER_OPS: Array<{ value: ChartFilter['op']; labelKey: NlsKey }> = [
  { value: 'is', labelKey: 'NLSChartFilterOpIs' },
  { value: 'isNot', labelKey: 'NLSChartFilterOpIsNot' },
  { value: 'in', labelKey: 'NLSChartFilterOpIn' },
  { value: 'gt', labelKey: 'NLSChartFilterOpGt' },
  { value: 'lt', labelKey: 'NLSChartFilterOpLt' },
  { value: 'between', labelKey: 'NLSChartFilterOpBetween' },
  { value: 'isEmpty', labelKey: 'NLSChartFilterOpIsEmpty' },
]

function convertFilterValueOnOpChange(
  newOp: ChartFilter['op'],
  oldVal: unknown,
  fieldType?: 'string' | 'number' | 'boolean' | 'date',
): unknown {
  if (newOp === 'isEmpty') {
    return ''
  }
  if (fieldType === 'boolean') {
    return true
  }
  if (newOp === 'in') {
    if (Array.isArray(oldVal) && oldVal.length > 0) {
      return oldVal.slice(0, CHART_MAX_FILTER_IN_ITEMS)
    }
    if (oldVal !== undefined && oldVal !== null && oldVal !== '') {
      return [oldVal]
    }
    return []
  }
  if (newOp === 'between') {
    if (Array.isArray(oldVal) && oldVal.length === 2) {
      return oldVal
    }
    if (fieldType === 'number') {
      const num = typeof oldVal === 'number' ? oldVal : 0
      return [num, num + 10]
    }
    return ['2026-01-01', '2026-12-31']
  }
  if (newOp === 'gt' || newOp === 'lt') {
    if (fieldType === 'date') {
      return typeof oldVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(oldVal) ? oldVal : '2026-01-01'
    }
    return typeof oldVal === 'number' ? oldVal : 0
  }
  // is or isNot
  if (Array.isArray(oldVal)) {
    return oldVal[0] ?? ''
  }
  if (fieldType === 'number') {
    return typeof oldVal === 'number' ? oldVal : 0
  }
  return typeof oldVal === 'string' ? oldVal : String(oldVal ?? '')
}

interface ChartFilterValueControlProps {
  workspaceId: string
  dataset: string
  filter: ChartFilter
  fieldDef?: DatasetField
  onChange: (val: unknown) => void
  autoFocus?: boolean
}

function ChartFilterValueControl({
  workspaceId,
  dataset,
  filter,
  fieldDef,
  onChange,
  autoFocus,
}: ChartFilterValueControlProps) {
  const { t } = useNls()
  const [items, setItems] = useState<ChartFieldValueItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let active = true
    if (fieldDef?.kind !== 'dimension' || fieldDef?.type === 'boolean') {
      setItems([])
      return
    }
    setLoading(true)
    setError(false)
    listChartFieldValues({
      workspaceId,
      dataset,
      field: filter.field,
      limit: 50,
    })
      .then((res) => {
        if (active) {
          setItems(res.values)
          setLoading(false)
        }
      })
      .catch(() => {
        if (active) {
          setError(true)
          setLoading(false)
        }
      })
    return () => {
      active = false
    }
  }, [workspaceId, dataset, filter.field, fieldDef?.kind, fieldDef?.type])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  const handleSearch = useCallback(
    (q: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        setLoading(true)
        listChartFieldValues({
          workspaceId,
          dataset,
          field: filter.field,
          query: q.trim() || undefined,
          limit: 50,
        })
          .then((res) => {
            setItems(res.values)
            setLoading(false)
          })
          .catch(() => {
            setError(true)
            setLoading(false)
          })
      }, 300)
    },
    [workspaceId, dataset, filter.field],
  )

  if (filter.op === 'isEmpty') {
    return null
  }

  if (fieldDef?.type === 'boolean') {
    const boolValue = filter.value === true || filter.value === 'true' ? 'true' : 'false'
    return (
      <Select
        ariaLabel="Filter value"
        value={boolValue}
        onChange={(val) => onChange(val === 'true')}
        options={[
          { value: 'true', label: t('NLSChartsBuilderValueYes') },
          { value: 'false', label: t('NLSChartsBuilderValueNo') },
        ]}
        size="sm"
      />
    )
  }

  if (filter.op === 'gt' || filter.op === 'lt') {
    const isDate = fieldDef?.type === 'date'
    return (
      <TextInput
        type={isDate ? 'date' : 'number'}
        aria-label="Filter value"
        value={String(filter.value ?? '')}
        onChange={(val) => onChange(isDate ? val : parseFloat(val) || 0)}
        placeholder={t('NLSChartsBuilderFilterValue')}
        data-testid="filter-value-input"
        autoFocus={autoFocus}
      />
    )
  }

  if (filter.op === 'between') {
    const isDate = fieldDef?.type === 'date'
    const valArr = Array.isArray(filter.value) ? filter.value : ['', '']
    return (
      <div className="chartFilterBetweenInputs">
        <TextInput
          type={isDate ? 'date' : 'number'}
          aria-label={t('NLSChartsBuilderValueFrom')}
          placeholder={t('NLSChartsBuilderValueFrom')}
          value={String(valArr[0] ?? '')}
          onChange={(val) => {
            const next0 = isDate ? val : parseFloat(val) || 0
            onChange([next0, valArr[1] ?? (isDate ? '2026-12-31' : 0)])
          }}
          data-testid="filter-between-from"
          autoFocus={autoFocus}
        />
        <TextInput
          type={isDate ? 'date' : 'number'}
          aria-label={t('NLSChartsBuilderValueTo')}
          placeholder={t('NLSChartsBuilderValueTo')}
          value={String(valArr[1] ?? '')}
          onChange={(val) => {
            const next1 = isDate ? val : parseFloat(val) || 0
            onChange([valArr[0] ?? (isDate ? '2026-01-01' : 0), next1])
          }}
          data-testid="filter-between-to"
        />
      </div>
    )
  }

  // Dimension fields (string) with distinct values from listChartFieldValues
  const options = (() => {
    const map = new Map<string, { value: string; label: string; searchText?: string }>()
    for (const item of items) {
      const valStr = String(item.value)
      const labelStr = item.label ?? (item.labelKey ? t(item.labelKey as NlsKey) : valStr)
      map.set(valStr, { value: valStr, label: labelStr, searchText: labelStr })
    }
    // Ensure existing selected value(s) have an option entry
    if (typeof filter.value === 'string' && filter.value && !map.has(filter.value)) {
      map.set(filter.value, { value: filter.value, label: filter.value, searchText: filter.value })
    } else if (Array.isArray(filter.value)) {
      for (const v of filter.value) {
        const vStr = String(v)
        if (vStr && !map.has(vStr)) {
          map.set(vStr, { value: vStr, label: vStr, searchText: vStr })
        }
      }
    }
    return Array.from(map.values())
  })()

  const emptyMsg = loading
    ? t('NLSChartsBuilderLoadingValues')
    : error
      ? t('NLSChartsBuilderLoadValuesError')
      : t('NLSChartsBuilderNoValuesFound')

  if (filter.op === 'in') {
    const selectedVals = Array.isArray(filter.value) ? filter.value.map(String) : []
    return (
      <MultiSelect<string>
        label={t('NLSChartsBuilderFilterValue')}
        ariaLabel="Filter value"
        use="filter"
        searchable
        forceCollapsed
        selectedValues={selectedVals}
        onChange={(next) => onChange(next.slice(0, CHART_MAX_FILTER_IN_ITEMS))}
        options={options}
        emptyLabel={emptyMsg}
        placeholder={loading ? t('NLSChartsBuilderLoadingValues') : t('NLSChartsBuilderFilterValue')}
        onSearch={handleSearch}
        size="sm"
      />
    )
  }

  // is or isNot
  return (
    <Select<string>
      ariaLabel="Filter value"
      searchable
      value={typeof filter.value === 'string' ? filter.value : String(filter.value ?? '')}
      onChange={(val) => onChange(val)}
      options={options}
      emptyLabel={emptyMsg}
      placeholder={loading ? t('NLSChartsBuilderLoadingValues') : t('NLSChartsBuilderFilterValue')}
      onSearch={handleSearch}
      size="sm"
    />
  )
}

const DEFAULT_SPEC = (workspaceId: string): ChartSpec => ({
  version: 1,
  id: '',
  workspaceId,
  title: '',
  subtitle: '',
  dataset: 'work',
  measure: { field: 'type', agg: 'count' },
  range: { kind: 'all' },
  filters: [],
  type: 'bar',
  style: {
    palette: 'theme',
    legend: 'auto',
    dataLabels: false,
    gridlines: true,
  },
  createdBy: '',
  createdAt: new Date().toISOString(),
  updatedBy: '',
  updatedAt: new Date().toISOString(),
})

function omitQuestionFieldId(measure: ChartMeasure): ChartMeasure {
  const next: ChartMeasure = { ...measure }
  delete next.questionFieldId
  return next
}

export function ChartBuilderDialog({
  open,
  workspaceId,
  initialSpec,
  initialStep,
  clientRows = [],
  onClose,
  onSaved,
  testId = 'chart-builder-dialog',
}: ChartBuilderDialogProps) {
  const { t } = useNls()
  const workspaceContext = useContext(WorkspaceContext)
  const canManage = workspaceContext ? workspaceContext.canPerform('chart.manage') : false

  const isEditing = Boolean(initialSpec?.id)
  const [activeStepIndex, setActiveStepIndex] = useState(() => {
    if (initialStep) {
      const idx = BUILDER_STEPS.indexOf(initialStep)
      if (idx >= 0) return idx
    }
    return 0
  })
  const [spec, setSpec] = useState<ChartSpec>(() => initialSpec ?? DEFAULT_SPEC(workspaceId))
  const [isDirty, setIsDirty] = useState(false)
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)

  // AI Ask Line
  const [askPrompt, setAskPrompt] = useState('')
  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)

  // Validation & Plan Limit
  const [validationErrors, setValidationErrors] = useState<ChartSpecError[]>([])
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Live preview state
  const [serverResult, setServerResult] = useState<ChartResult | null>(null)
  const [isServerResult, setIsServerResult] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const querySeqRef = useRef(0)
  const firstFilterRef = useRef<HTMLDivElement | null>(null)

  // Single form resolution for forms dataset question pickers
  const singleFormId = useMemo(() => {
    if (spec.dataset !== 'forms') return null
    const formFilters = (spec.filters || []).filter(
      (f) => f && f.field === 'form' && f.op === 'is' && typeof f.value === 'string' && f.value.trim() !== '',
    )
    return formFilters.length === 1 ? String(formFilters[0].value).trim() : null
  }, [spec.dataset, spec.filters])

  const [selectedFormDef, setSelectedFormDef] = useState<FormDefinition | null>(null)

  useEffect(() => {
    if (!singleFormId) {
      setSelectedFormDef(null)
      return
    }
    let active = true
    getFormDefinition(singleFormId)
      .then((def) => {
        if (!active) return
        if (def && def.workspaceId === workspaceId) {
          setSelectedFormDef(def)
        } else {
          setSelectedFormDef(null)
        }
      })
      .catch(() => {
        if (active) setSelectedFormDef(null)
      })
    return () => {
      active = false
    }
  }, [singleFormId, workspaceId])

  const choiceQuestions = useMemo(() => {
    if (!selectedFormDef?.fields) return []
    return selectedFormDef.fields
      .filter((f) => f.type === 'DROPDOWN' || f.type === 'CHECKBOX')
      .map((f) => ({
        id: f.id,
        label: f.label || f.id,
      }))
  }, [selectedFormDef])

  const numberQuestions = useMemo(() => {
    if (!selectedFormDef?.fields) return []
    return selectedFormDef.fields
      .filter((f) => f.type === 'NUMBER')
      .map((f) => ({
        id: f.id,
        label: f.label || f.id,
      }))
  }, [selectedFormDef])

  // Sync initialSpec when dialog opens or initialSpec changes
  useEffect(() => {
    if (initialSpec) {
      setSpec(initialSpec)
      setIsDirty(false)
    } else {
      setSpec(DEFAULT_SPEC(workspaceId))
      setIsDirty(false)
    }
    setValidationErrors([])
    setSaveError(null)
    setDraftError(null)
    if (initialStep) {
      const idx = BUILDER_STEPS.indexOf(initialStep)
      if (idx >= 0) setActiveStepIndex(idx)
      else setActiveStepIndex(0)
    } else {
      setActiveStepIndex(0)
    }
  }, [initialSpec, workspaceId, open, initialStep])

  useEffect(() => {
    if (open && BUILDER_STEPS[activeStepIndex] === 'Filters' && initialStep === 'Filters') {
      const timer = setTimeout(() => {
        const el = firstFilterRef.current?.querySelector<HTMLElement>(
          'button, input:not([type="hidden"]), [tabindex="0"]'
        )
        el?.focus()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [open, activeStepIndex, initialStep])

  // Current surface detection
  const currentSurface = useMemo(() => {
    if (typeof document !== 'undefined') {
      const isDark =
        document.documentElement.getAttribute('data-theme')?.includes('dark') ||
        document.body.classList.contains('dark') ||
        (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches)
      return isDark ? SURFACE_DARK : SURFACE_LIGHT
    }
    return SURFACE_LIGHT
  }, [])

  // Live Preview Computation: client engine immediately, then server query debounced ~600ms
  const clientResult = useMemo<ChartResult | null>(() => {
    if (clientRows && clientRows.length > 0 && spec.dataset === 'work') {
      try {
        return runChartEngine(clientRows, spec)
      } catch {
        return null
      }
    }
    return null
  }, [clientRows, spec])

  useEffect(() => {
    if (!open) return
    const currentSeq = ++querySeqRef.current
    setPreviewLoading(true)

    const timer = setTimeout(async () => {
      try {
        const res = await runChartQuery({ workspaceId, spec })
        if (currentSeq === querySeqRef.current) {
          setServerResult(res)
          setIsServerResult(true)
          setPreviewLoading(false)
        }
      } catch {
        if (currentSeq === querySeqRef.current) {
          setPreviewLoading(false)
        }
      }
    }, 600)

    return () => {
      clearTimeout(timer)
    }
  }, [spec, workspaceId, open])

  const previewData: ChartResult | null = isServerResult && serverResult ? serverResult : clientResult
  const isPreviewClientOnly = !isServerResult || !serverResult

  // Step change / update helpers
  const updateSpec = useCallback((patch: Partial<ChartSpec>) => {
    setSpec((prev) => ({ ...prev, ...patch }))
    setIsDirty(true)
    setIsServerResult(false)
  }, [])

  const updateMeasure = useCallback((patch: Partial<ChartMeasure>) => {
    setSpec((prev) => {
      let nextMeasure: ChartMeasure = { ...prev.measure, ...patch }
      let nextSecondMeasure = prev.secondMeasure

      if (patch.field !== undefined) {
        if (patch.field === 'numberField') {
          if (prev.secondMeasure?.field === 'numberField' && prev.secondMeasure.questionFieldId) {
            nextMeasure = { ...nextMeasure, questionFieldId: prev.secondMeasure.questionFieldId }
          }
        } else {
          nextMeasure = omitQuestionFieldId(nextMeasure)
        }
      }

      if (patch.questionFieldId !== undefined && prev.secondMeasure?.field === 'numberField') {
        nextSecondMeasure = { ...prev.secondMeasure, questionFieldId: patch.questionFieldId }
      }

      return {
        ...prev,
        measure: nextMeasure,
        secondMeasure: nextSecondMeasure,
      }
    })
    setIsDirty(true)
    setIsServerResult(false)
  }, [])

  const updateSecondMeasure = useCallback((patch: Partial<ChartMeasure> | null) => {
    setSpec((prev) => {
      if (!patch) {
        return {
          ...prev,
          secondMeasure: undefined,
        }
      }

      let nextSecondMeasure: ChartMeasure = {
        ...(prev.secondMeasure ?? { field: '', agg: 'count' }),
        ...patch,
      }

      if (patch.field !== undefined) {
        if (patch.field === 'numberField') {
          if (prev.measure.field === 'numberField' && prev.measure.questionFieldId) {
            nextSecondMeasure = { ...nextSecondMeasure, questionFieldId: prev.measure.questionFieldId }
          }
        } else {
          nextSecondMeasure = omitQuestionFieldId(nextSecondMeasure)
        }
      }

      let nextMeasure = prev.measure
      if (patch.questionFieldId !== undefined && prev.measure.field === 'numberField') {
        nextMeasure = { ...prev.measure, questionFieldId: patch.questionFieldId }
      }

      return {
        ...prev,
        measure: nextMeasure,
        secondMeasure: nextSecondMeasure,
      }
    })
    setIsDirty(true)
    setIsServerResult(false)
  }, [])

  const updateStyle = useCallback((patch: Partial<ChartSpec['style']>) => {
    setSpec((prev) => ({
      ...prev,
      style: { ...prev.style, ...patch },
    }))
    setIsDirty(true)
  }, [])

  // AI draft handler (Ask Line in builder)
  const handleDraftWithLine = useCallback(async () => {
    if (!askPrompt.trim()) return
    setDrafting(true)
    setDraftError(null)
    try {
      const res = await draftChartFromText(workspaceId, askPrompt.trim())
      if (res.ok) {
        setSpec((prev) => ({
          ...res.spec,
          id: prev.id,
          workspaceId: prev.workspaceId,
        }))
        setIsDirty(true)
        setIsServerResult(false)
      } else {
        // Show reason or "I can't chart that yet" and leave steps as they were
        setDraftError(t('NLSChartsCantChartYet'))
      }
    } catch (err: unknown) {
      const errObj = err as { code?: string; message?: string }
      if (errObj?.message?.includes('credit') || errObj?.code === 'credits.insufficient') {
        setDraftError(t('NLSChartsCreditsInsufficient'))
      } else {
        setDraftError(t('NLSChartsCantChartYet'))
      }
    } finally {
      setDrafting(false)
    }
  }, [askPrompt, workspaceId, t])

  // Save handler
  const handleSave = useCallback(
    async (asCopy = false) => {
      setSaveError(null)
      const candidateSpec: ChartSpec = {
        ...spec,
        workspaceId,
        id: asCopy || !spec.id ? `spec-${Date.now()}` : spec.id,
        title: asCopy ? `${spec.title || 'Chart'} (Copy)` : (spec.title || 'Untitled chart'),
      }

      const validation = chartSpecSchema(candidateSpec)
      if (!validation.ok) {
        setValidationErrors(validation.errors)
        return
      }
      setValidationErrors([])
      setIsSaving(true)

      try {
        if (!asCopy && isEditing && spec.id) {
          await updateChart(workspaceId, spec.id, candidateSpec)
          setIsDirty(false)
          onSaved(spec.id, candidateSpec)
          onClose()
        } else {
          const res = await createChart(workspaceId, candidateSpec)
          setIsDirty(false)
          onSaved(res.chartId, candidateSpec)
          onClose()
        }
      } catch (err: unknown) {
        // Map limit error to NLS message
        const errObj = err as { details?: { code?: string; limit?: number }; message?: string }
        if (errObj?.details?.code === 'charts.limit' || errObj?.message?.includes('limit')) {
          const limit = errObj?.details?.limit
          if (limit === 5) {
            setSaveError(t('NLSChartsLimitReachedFree'))
          } else if (limit === 50) {
            setSaveError(t('NLSChartsLimitReachedPaid'))
          } else {
            setSaveError(t('NLSChartsLimitReachedGeneric'))
          }
        } else {
          setSaveError(errObj?.message || String(err))
        }
      } finally {
        setIsSaving(false)
      }
    },
    [spec, workspaceId, isEditing, onSaved, onClose, t],
  )

  const handleCancelClick = useCallback(() => {
    if (isDirty) {
      setConfirmDiscardOpen(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  // Filter validation errors by step
  const errorsForStep = useMemo(() => {
    const map: Record<BuilderStep, string[]> = {
      Data: [],
      Type: [],
      Measure: [],
      'Break down': [],
      Time: [],
      Filters: [],
      Style: [],
    }
    for (const err of validationErrors) {
      if (err.path.startsWith('title') || err.path.startsWith('dataset') || err.code.startsWith('dataset')) {
        map.Data.push(err.message)
      } else if (err.path.startsWith('measure') || err.code.startsWith('measure') || err.path.startsWith('secondMeasure') || err.code.startsWith('secondMeasure')) {
        map.Measure.push(err.message)
      } else if (err.path.startsWith('breakdown') || err.code.startsWith('breakdown')) {
        map['Break down'].push(err.message)
      } else if (err.path.startsWith('time') || err.path.startsWith('range') || err.code.startsWith('time') || err.code.startsWith('range')) {
        map.Time.push(err.message)
      } else if (err.path.startsWith('filter') || err.code.startsWith('filter')) {
        map.Filters.push(err.message)
      } else if (err.path.startsWith('type') || err.path.startsWith('target') || err.code.startsWith('type') || err.code.startsWith('target')) {
        map.Type.push(err.message)
      } else if (err.path.startsWith('style') || err.code.startsWith('style')) {
        map.Style.push(err.message)
      }
    }
    return map
  }, [validationErrors])

  // Color guard checks
  const customColor = spec.style.colors?.[0] || spec.style.seriesColor?.[Object.keys(spec.style.seriesColor || {})[0]] || ''
  const colorGuardCheck = useMemo(() => {
    if (spec.style.palette !== 'custom' || !customColor) return null
    return checkChartColor(customColor, currentSurface)
  }, [spec.style.palette, customColor, currentSurface])

  const failingOtherThemes = useMemo(() => {
    if (spec.style.palette !== 'custom' || !customColor) return []
    return OTHER_THEMES.filter((ot) => !checkChartColor(customColor, ot.surface).passes).map((ot) => ot.name)
  }, [spec.style.palette, customColor])

  const handleFixReadableColor = useCallback(() => {
    if (!customColor) return
    const fixedHex = suggestReadable(customColor, currentSurface)
    updateStyle({
      colors: [fixedHex],
      seriesColor: spec.style.seriesColor
        ? Object.fromEntries(Object.keys(spec.style.seriesColor).map((k) => [k, fixedHex]))
        : undefined,
    })
  }, [customColor, currentSurface, updateStyle, spec.style.seriesColor])

  // Security guard for non-manage users (Component-level guard!)
  if (!canManage) {
    return (
      <AppDialog
        open={open}
        title={t('NLSChartsPermissionDenied')}
        onDismiss={onClose}
        size="sm"
        actions={[
          {
            label: t('NLSChartsCancel'),
            variant: 'secondary',
            onClick: onClose,
          },
        ]}
      >
        <p className="chartPermissionDeniedText">{t('NLSChartsPermissionDenied')}</p>
      </AppDialog>
    )
  }

  const currentDatasetDef = DATASET_CATALOG[spec.dataset]
  const fields = currentDatasetDef?.fields ?? []
  const currentStep = BUILDER_STEPS[activeStepIndex]

  return (
    <>
      <AppDialog
        open={open}
        title={isEditing ? t('NLSChartsBuilderTitleEdit') : t('NLSChartsBuilderTitleNew')}
        onDismiss={handleCancelClick}
        size="lg"
        data-testid={testId}
        actions={[
          {
            label: t('NLSChartsCancel'),
            variant: 'secondary',
            onClick: handleCancelClick,
            disabled: isSaving,
          },
          ...(isEditing
            ? [
                {
                  label: t('NLSChartsSaveAsCopy'),
                  variant: 'ghost' as const,
                  onClick: () => void handleSave(true),
                  disabled: isSaving,
                  testId: 'chart-builder-save-as-copy-btn',
                },
              ]
            : []),
          {
            label: t('NLSChartsSaveToLibrary'),
            variant: 'primary',
            onClick: () => void handleSave(false),
            disabled: isSaving,
            loading: isSaving,
            testId: 'chart-builder-save-btn',
          },
        ]}
      >
        <div className="chartBuilderContainer">
          {saveError && (
            <div className="chartBuilderErrorBanner" role="alert" data-testid="chart-builder-save-error">
              {saveError}
            </div>
          )}

          <div className="chartBuilderLayout">
            {/* Left Column: Step Navigation & Step Controls */}
            <div className="chartBuilderLeftCol">
              <nav className="chartBuilderStepNav" aria-label="Builder steps">
                {BUILDER_STEPS.map((step, idx) => (
                  <button
                    key={step}
                    type="button"
                    className={`chartBuilderStepTab ${idx === activeStepIndex ? 'is-active' : ''}`}
                    onClick={() => setActiveStepIndex(idx)}
                    data-testid={`step-tab-${step.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span>{idx + 1}.</span> {step}
                    {errorsForStep[step].length > 0 && <span className="chartStepErrorDot" title="Errors on this step" />}
                  </button>
                ))}
              </nav>

              <div className="chartBuilderStepBody">
                {errorsForStep[currentStep].length > 0 && (
                  <div className="chartStepErrors" data-testid="chart-step-errors">
                    {errorsForStep[currentStep].map((e, i) => (
                      <p key={i} className="chartStepErrorItem">{e}</p>
                    ))}
                  </div>
                )}

                {/* STEP 0: DATA */}
                {currentStep === 'Data' && (
                  <div className="chartStepContent" data-testid="step-content-data">
                    {/* Ask Line In Builder */}
                    <div className="chartAskLineBox" data-testid="ask-line-box">
                      <header className="chartAskLineHeader">
                        <AutoAwesomeRoundedIcon fontSize="small" />
                        <strong>Line</strong>
                        <span className="chartCreditCost">
                          {t('NLSChartsAskLineCost', { credits: AI_CHART_PURPOSES.AI_CHART_DRAFT.fixedCredits })}
                        </span>
                      </header>
                      <div className="chartAskLineInputRow">
                        <TextInput
                          value={askPrompt}
                          onChange={setAskPrompt}
                          placeholder={t('NLSChartsAskLinePrompt')}
                          aria-label={t('NLSChartsAskLinePrompt')}
                          data-testid="ask-line-input"
                        />
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void handleDraftWithLine()}
                          disabled={drafting || !askPrompt.trim()}
                          loading={drafting}
                          data-testid="ask-line-submit-btn"
                        >
                          {t('NLSChartsAskLineButton')}
                        </Button>
                      </div>
                      {draftError && (
                        <p className="chartAskLineError" role="alert" data-testid="ask-line-error">
                          {draftError}
                        </p>
                      )}
                    </div>

                    <div className="chartFieldRow">
                      <label htmlFor="chart-title-input">Title</label>
                      <TextInput
                        id="chart-title-input"
                        value={spec.title}
                        onChange={(val) => updateSpec({ title: val })}
                        placeholder="Chart title"
                        data-testid="chart-title-input"
                      />
                    </div>

                    <div className="chartFieldRow">
                      <label htmlFor="chart-subtitle-input">Subtitle</label>
                      <TextInput
                        id="chart-subtitle-input"
                        value={spec.subtitle ?? ''}
                        onChange={(val) => updateSpec({ subtitle: val })}
                        placeholder="Optional subtitle"
                        data-testid="chart-subtitle-input"
                      />
                    </div>

                    <div className="chartFieldRow">
                      <label htmlFor="chart-dataset-select">Dataset</label>
                      <Select
                        id="chart-dataset-select"
                        ariaLabel="Dataset"
                        value={spec.dataset}
                        onChange={(val) => {
                          const newDef = DATASET_CATALOG[val as DatasetKey]
                          if (newDef) {
                            updateSpec({
                              dataset: val as DatasetKey,
                              measure: newDef.defaultMeasure,
                            })
                          }
                        }}
                        options={Object.keys(DATASET_CATALOG).map((key) => {
                          const def = DATASET_CATALOG[key as DatasetKey]
                          return {
                            value: def.key,
                            label: `${t(def.labelKey as never) || def.key}${def.available ? '' : ` (${def.unavailableReason || 'Unavailable'})`}`,
                            disabled: !def.available,
                          }
                        })}
                      />
                    </div>
                  </div>
                )}

                {/* STEP 1: MEASURE */}
                {currentStep === 'Measure' && (
                  <div className="chartStepContent" data-testid="step-content-measure">
                    <div className="chartFieldRow">
                      <label htmlFor="chart-measure-field">Measure Field</label>
                      <Select
                        id="chart-measure-field"
                        ariaLabel="Measure Field"
                        value={spec.measure.field}
                        onChange={(val) => updateMeasure({ field: val })}
                        options={fields.map((f) => ({
                          value: f.name,
                          label: `${t(f.labelKey as NlsKey)} (${f.kind})`,
                        }))}
                      />
                    </div>

                    <div className="chartFieldRow">
                      <label htmlFor="chart-measure-agg">Aggregation</label>
                      <Select
                        id="chart-measure-agg"
                        ariaLabel="Aggregation"
                        value={spec.measure.agg}
                        onChange={(val) => updateMeasure({ agg: val as ChartMeasureAgg })}
                        options={[
                          { value: 'count', label: 'Count' },
                          { value: 'sum', label: 'Sum' },
                          { value: 'avg', label: 'Average' },
                          { value: 'min', label: 'Min' },
                          { value: 'max', label: 'Max' },
                          { value: 'distinct', label: 'Distinct count' },
                        ]}
                      />
                    </div>

                    {spec.dataset === 'forms' && spec.measure.field === 'numberField' && singleFormId && (
                      <div className="chartFieldRow">
                        <label htmlFor="chart-measure-question">{t('NLSChartNumberSelectLabel')}</label>
                        <Select
                          id="chart-measure-question"
                          ariaLabel={t('NLSChartNumberSelectLabel')}
                          value={spec.measure.questionFieldId ?? ''}
                          onChange={(val) => updateMeasure({ questionFieldId: val || undefined })}
                          options={[
                            { value: '', label: t('NLSChartNumberSelectDefault') },
                            ...numberQuestions.map((q) => ({
                              value: q.id,
                              label: q.label,
                            })),
                          ]}
                        />
                      </div>
                    )}

                    {/* Second measure option (required for combo) */}
                    <div className="chartFieldRow">
                      <Toggle
                        checked={Boolean(spec.secondMeasure)}
                        onChange={(checked) => {
                          if (checked) {
                            updateSecondMeasure({ field: fields[0]?.name ?? 'type', agg: 'count' })
                          } else {
                            updateSecondMeasure(null)
                          }
                        }}
                        label="Enable second measure (for dual-axis / combo)"
                        data-testid="second-measure-toggle"
                      />
                    </div>

                    {spec.secondMeasure && (
                      <div className="chartSubSection">
                        <div className="chartFieldRow">
                          <label htmlFor="chart-second-measure-field">Second Measure Field</label>
                          <Select
                            id="chart-second-measure-field"
                            ariaLabel="Second Measure Field"
                            value={spec.secondMeasure.field}
                            onChange={(val) => updateSecondMeasure({ field: val })}
                            options={fields.map((f) => ({
                              value: f.name,
                              label: `${t(f.labelKey as NlsKey)} (${f.kind})`,
                            }))}
                          />
                        </div>
                        <div className="chartFieldRow">
                          <label htmlFor="chart-second-measure-agg">Second Aggregation</label>
                          <Select
                            id="chart-second-measure-agg"
                            ariaLabel="Second Aggregation"
                            value={spec.secondMeasure.agg}
                            onChange={(val) => updateSecondMeasure({ agg: val as ChartMeasureAgg })}
                            options={[
                              { value: 'count', label: 'Count' },
                              { value: 'sum', label: 'Sum' },
                              { value: 'avg', label: 'Average' },
                              { value: 'min', label: 'Min' },
                              { value: 'max', label: 'Max' },
                              { value: 'distinct', label: 'Distinct count' },
                            ]}
                          />
                        </div>

                        {spec.dataset === 'forms' &&
                          spec.secondMeasure.field === 'numberField' &&
                          spec.measure.field !== 'numberField' &&
                          singleFormId && (
                            <div className="chartFieldRow">
                              <label htmlFor="chart-measure-question">{t('NLSChartNumberSelectLabel')}</label>
                              <Select
                                id="chart-measure-question"
                                ariaLabel={t('NLSChartNumberSelectLabel')}
                                value={spec.secondMeasure.questionFieldId ?? ''}
                                onChange={(val) => updateSecondMeasure({ questionFieldId: val || undefined })}
                                options={[
                                  { value: '', label: t('NLSChartNumberSelectDefault') },
                                  ...numberQuestions.map((q) => ({
                                    value: q.id,
                                    label: q.label,
                                  })),
                                ]}
                              />
                            </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* STEP 2: BREAK DOWN */}
                {currentStep === 'Break down' && (
                  <div className="chartStepContent" data-testid="step-content-breakdown">
                    <div className="chartFieldRow">
                      <label htmlFor="chart-breakdown-field">Breakdown by</label>
                      <Select
                        id="chart-breakdown-field"
                        ariaLabel="Breakdown by"
                        value={spec.breakdown?.field ?? ''}
                        onChange={(val) => {
                          if (!val) {
                            updateSpec({ breakdown: undefined })
                          } else {
                            updateSpec({
                              breakdown: {
                                field: val,
                                topN: spec.breakdown?.topN ?? 10,
                                other: spec.breakdown?.other ?? true,
                              },
                            })
                          }
                        }}
                        options={[
                          { value: '', label: 'None (overall total)' },
                          ...fields
                            .filter((f) => f.kind === 'dimension')
                            .map((f) => ({ value: f.name, label: t(f.labelKey as NlsKey) })),
                        ]}
                      />
                    </div>

                    {spec.dataset === 'forms' && spec.breakdown?.field === 'questionAnswer' && singleFormId && (
                      <div className="chartFieldRow">
                        <label htmlFor="chart-breakdown-question">{t('NLSChartQuestionSelectLabel')}</label>
                        <Select
                          id="chart-breakdown-question"
                          ariaLabel={t('NLSChartQuestionSelectLabel')}
                          value={spec.breakdown.questionFieldId ?? ''}
                          onChange={(val) => {
                            if (!spec.breakdown) return
                            updateSpec({
                              breakdown: {
                                ...spec.breakdown,
                                questionFieldId: val || undefined,
                              },
                            })
                          }}
                          options={[
                            { value: '', label: t('NLSChartQuestionSelectDefault') },
                            ...choiceQuestions.map((q) => ({
                              value: q.id,
                              label: q.label,
                            })),
                          ]}
                        />
                      </div>
                    )}

                    {spec.breakdown && (
                      <>
                        <div className="chartFieldRow">
                          <label htmlFor="chart-breakdown-topn">Top N groups</label>
                          <TextInput
                            id="chart-breakdown-topn"
                            type="number"
                            value={String(spec.breakdown.topN ?? 10)}
                            onChange={(val) => {
                              const n = parseInt(val, 10)
                              if (!isNaN(n) && n >= 1 && n <= 20) {
                                updateSpec({ breakdown: { ...spec.breakdown!, topN: n } })
                              }
                            }}
                            data-testid="chart-breakdown-topn-input"
                          />
                        </div>

                        <div className="chartFieldRow">
                          <Toggle
                            checked={spec.breakdown.other !== false}
                            onChange={(checked) =>
                              updateSpec({ breakdown: { ...spec.breakdown!, other: checked } })
                            }
                            label='Group remaining into "Other"'
                            data-testid="chart-breakdown-other-toggle"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* STEP 3: TIME */}
                {currentStep === 'Time' && (
                  <div className="chartStepContent" data-testid="step-content-time">
                    <div className="chartFieldRow">
                      <label htmlFor="chart-time-field">Date / Time Field</label>
                      <Select
                        id="chart-time-field"
                        ariaLabel="Date / Time Field"
                        value={spec.time?.field ?? currentDatasetDef?.defaultTimeField ?? ''}
                        onChange={(val) => {
                          updateSpec({
                            time: {
                              field: val,
                              bucket: spec.time?.bucket ?? 'day',
                            },
                          })
                        }}
                        options={fields
                          .filter((f) => f.kind === 'time')
                          .map((f) => ({ value: f.name, label: t(f.labelKey as NlsKey) }))}
                      />
                    </div>

                    <div className="chartFieldRow">
                      <label htmlFor="chart-time-bucket">Bucket</label>
                      <Select
                        id="chart-time-bucket"
                        ariaLabel="Bucket"
                        value={spec.time?.bucket ?? 'day'}
                        onChange={(val) => {
                          updateSpec({
                            time: {
                              field: spec.time?.field ?? currentDatasetDef?.defaultTimeField ?? 'created',
                              bucket: val as never,
                            },
                          })
                        }}
                        options={[
                          { value: 'day', label: 'Day' },
                          { value: 'week', label: 'Week' },
                          { value: 'month', label: 'Month' },
                          { value: 'quarter', label: 'Quarter' },
                        ]}
                      />
                    </div>

                    <div className="chartFieldRow">
                      <label htmlFor="chart-range-kind">Date Range</label>
                      <Select
                        id="chart-range-kind"
                        ariaLabel="Date Range"
                        value={spec.range.kind}
                        onChange={(val) => {
                          if (val === 'all') {
                            updateSpec({ range: { kind: 'all' } })
                          } else if (val === 'relative') {
                            updateSpec({ range: { kind: 'relative', last: 30, unit: 'day' } })
                          } else {
                            updateSpec({ range: { kind: 'absolute', from: '2026-01-01', to: '2026-12-31' } })
                          }
                        }}
                        options={[
                          { value: 'all', label: 'All time' },
                          { value: 'relative', label: 'Relative (last N days/weeks/months)' },
                          { value: 'absolute', label: 'Absolute (specific dates)' },
                        ]}
                      />
                    </div>

                    {spec.range.kind === 'relative' && (
                      <div className="chartInlineInputs">
                        <TextInput
                          type="number"
                          value={String(spec.range.last)}
                          onChange={(val) => {
                            const n = parseInt(val, 10) || 1
                            const currentUnit = (spec.range as { unit?: 'day' | 'week' | 'month' }).unit || 'day'
                            updateSpec({ range: { kind: 'relative', last: n, unit: currentUnit } })
                          }}
                          data-testid="chart-range-last-input"
                        />
                        <Select
                          ariaLabel="Relative range unit"
                          value={spec.range.unit}
                          onChange={(val) => {
                            const currentLast = (spec.range as { last?: number }).last ?? 30
                            updateSpec({ range: { kind: 'relative', last: currentLast, unit: val as never } })
                          }}
                          options={[
                            { value: 'day', label: 'Days' },
                            { value: 'week', label: 'Weeks' },
                            { value: 'month', label: 'Months' },
                          ]}
                        />
                      </div>
                    )}

                    <div className="chartFieldRow">
                      <Toggle
                        checked={spec.compare === 'previousPeriod'}
                        onChange={(checked) => updateSpec({ compare: checked ? 'previousPeriod' : undefined })}
                        label="Compare to previous period"
                        data-testid="chart-compare-toggle"
                      />
                    </div>
                  </div>
                )}

                {/* STEP 4: FILTERS */}
                {currentStep === 'Filters' && (
                  <div className="chartStepContent" data-testid="step-content-filters">
                    <header className="chartFilterHeader">
                      <strong>{t('NLSChartsBuilderFiltersCount', { count: spec.filters.length })}</strong>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          const newFilter: ChartFilter = {
                            field: fields[0]?.name ?? 'status',
                            op: 'is',
                            value: '',
                          }
                          updateSpec({ filters: [...spec.filters, newFilter] })
                        }}
                        data-testid="add-filter-btn"
                      >
                        {t('NLSChartsBuilderAddFilter')}
                      </Button>
                    </header>

                    {spec.filters.map((filter, idx) => {
                      const fieldDef = fields.find((f) => f.name === filter.field)
                      return (
                        <div
                          key={idx}
                          ref={idx === 0 ? firstFilterRef : undefined}
                          className="chartFilterRow"
                          data-testid={`filter-row-${idx}`}
                        >
                          <Select
                            ariaLabel="Filter field"
                            value={filter.field}
                            onChange={(val) => {
                              const next = [...spec.filters]
                              const targetField = fields.find((f) => f.name === val)
                              let validOp = filter.op
                              if (
                                targetField?.type === 'boolean' &&
                                validOp !== 'is' &&
                                validOp !== 'isNot' &&
                                validOp !== 'isEmpty'
                              ) {
                                validOp = 'is'
                              }
                              const nextVal = convertFilterValueOnOpChange(
                                validOp,
                                filter.value,
                                targetField?.type,
                              )
                              next[idx] = { ...next[idx], field: val, op: validOp, value: nextVal as never }
                              updateSpec({ filters: next })
                            }}
                            options={fields.map((f) => ({ value: f.name, label: t(f.labelKey as NlsKey) }))}
                          />
                          <Select
                            ariaLabel="Filter operator"
                            value={filter.op}
                            onChange={(val) => {
                              const next = [...spec.filters]
                              const nextOp = val as ChartFilter['op']
                              const nextVal = convertFilterValueOnOpChange(
                                nextOp,
                                filter.value,
                                fieldDef?.type,
                              )
                              next[idx] = { ...next[idx], op: nextOp, value: nextVal as never }
                              updateSpec({ filters: next })
                            }}
                            options={FILTER_OPS.map((op) => ({
                              value: op.value,
                              label: t(op.labelKey),
                            }))}
                          />
                          <ChartFilterValueControl
                            workspaceId={workspaceId}
                            dataset={spec.dataset}
                            filter={filter}
                            fieldDef={fieldDef}
                            onChange={(val) => {
                              const next = [...spec.filters]
                              next[idx] = { ...next[idx], value: val as never }
                              updateSpec({ filters: next })
                            }}
                            autoFocus={idx === 0 && initialStep === 'Filters'}
                          />
                          <Button
                            size="sm"
                            variant="tertiary"
                            onClick={() => {
                              const next = spec.filters.filter((_, i) => i !== idx)
                              updateSpec({ filters: next })
                            }}
                          >
                            {t('NLSChartsBuilderFilterRemove')}
                          </Button>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* STEP 5: TYPE */}
                {currentStep === 'Type' && (
                  <div className="chartStepContent" data-testid="step-content-type">
                    <div className="chartTypeGrid">
                      {VALID_TYPES.map((typeKey) => {
                        let disabled = false
                        let reason = ''

                        if ((typeKey === 'gauge' || typeKey === 'progress' || typeKey === 'targetActual') && !spec.target) {
                          disabled = true
                          reason = t('NLSChartsTypeDisabledTargetRequired')
                        } else if (typeKey === 'combo' && !spec.secondMeasure) {
                          disabled = true
                          reason = t('NLSChartsTypeDisabledSecondMeasureRequired')
                        }

                        return (
                          <button
                            key={typeKey}
                            type="button"
                            className={`chartTypeOption ${spec.type === typeKey ? 'is-selected' : ''} ${disabled ? 'is-disabled' : ''}`}
                            disabled={disabled}
                            title={disabled ? reason : typeKey}
                            onClick={() => updateSpec({ type: typeKey })}
                            data-testid={`chart-type-option-${typeKey}`}
                          >
                            <strong>{typeKey}</strong>
                            {disabled && <small className="chartTypeDisabledReason">{reason}</small>}
                          </button>
                        )
                      })}
                    </div>

                    {/* Target configuration section */}
                    <div className="chartTargetConfig">
                      <header>
                        <strong>Target (required for Gauge, Progress, Target vs Actual)</strong>
                      </header>
                      <div className="chartFieldRow">
                        <Toggle
                          checked={Boolean(spec.target)}
                          onChange={(checked) => {
                            if (checked) {
                              updateSpec({ target: { kind: 'number', value: 100 } })
                            } else {
                              updateSpec({ target: undefined })
                            }
                          }}
                          label="Configure Target"
                          data-testid="target-toggle"
                        />
                      </div>
                      {spec.target && (
                        <div className="chartFieldRow">
                          <label htmlFor="target-value-input">Target Value</label>
                          <TextInput
                            id="target-value-input"
                            type="number"
                            value={String(spec.target.kind === 'number' ? spec.target.value : 100)}
                            onChange={(val) => {
                              const n = parseFloat(val) || 0
                              updateSpec({ target: { kind: 'number', value: n } })
                            }}
                            data-testid="target-value-input"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 6: STYLE */}
                {currentStep === 'Style' && (
                  <div className="chartStepContent" data-testid="step-content-style">
                    <div className="chartFieldRow">
                      <label htmlFor="chart-palette-select">Color Palette</label>
                      <Select
                        id="chart-palette-select"
                        ariaLabel="Color Palette"
                        value={spec.style.palette}
                        onChange={(val) => updateStyle({ palette: val as 'theme' | 'custom' })}
                        options={[
                          { value: 'theme', label: 'Theme colors (var(--chart-1..8))' },
                          { value: 'custom', label: 'Custom colors' },
                        ]}
                      />
                    </div>

                    {/* Custom colour & Colour guard */}
                    {spec.style.palette === 'custom' && (
                      <div className="chartCustomColorSection" data-testid="custom-color-guard">
                        <div className="chartFieldRow">
                          <label htmlFor="custom-color-input">Custom Hex Color</label>
                          <TextInput
                            id="custom-color-input"
                            value={customColor || ['#', '3b', '82', 'f6'].join('')}
                            onChange={(val) => {
                              updateStyle({
                                colors: [val],
                                seriesColor: spec.style.seriesColor
                                  ? Object.fromEntries(Object.keys(spec.style.seriesColor).map((k) => [k, val]))
                                  : undefined,
                              })
                            }}
                            data-testid="custom-color-input"
                          />
                        </div>

                        {colorGuardCheck && !colorGuardCheck.passes && (
                          <div className="chartColorGuardWarningBox" role="alert" data-testid="color-guard-warning">
                            <WarningAmberRoundedIcon fontSize="small" />
                            <div className="chartColorGuardContent">
                              <p>{t('NLSChartsColorGuardWarning')}</p>
                              {failingOtherThemes.length > 0 && (
                                <p className="chartFailingThemes">
                                  {t('NLSChartsColorGuardOtherThemes', { themes: failingOtherThemes.join(', ') })}
                                </p>
                              )}
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={handleFixReadableColor}
                                data-testid="use-readable-shade-btn"
                              >
                                {t('NLSChartsUseReadableShade')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="chartFieldRow">
                      <label htmlFor="chart-legend-select">Legend</label>
                      <Select
                        id="chart-legend-select"
                        ariaLabel="Legend"
                        value={spec.style.legend}
                        onChange={(val) => updateStyle({ legend: val as never })}
                        options={[
                          { value: 'auto', label: 'Auto' },
                          { value: 'top', label: 'Top' },
                          { value: 'bottom', label: 'Bottom' },
                          { value: 'right', label: 'Right' },
                          { value: 'off', label: 'Off' },
                        ]}
                      />
                    </div>

                    <div className="chartFieldRow">
                      <Toggle
                        checked={spec.style.dataLabels}
                        onChange={(checked) => updateStyle({ dataLabels: checked })}
                        label="Data labels"
                        data-testid="chart-datalabels-toggle"
                      />
                    </div>

                    <div className="chartFieldRow">
                      <Toggle
                        checked={spec.style.gridlines}
                        onChange={(checked) => updateStyle({ gridlines: checked })}
                        label="Grid lines"
                        data-testid="chart-gridlines-toggle"
                      />
                    </div>

                    {/* Per-type specific options (§3.3) */}
                    {spec.type === 'bar' && (
                      <div className="chartFieldRow">
                        <label htmlFor="bar-orientation-select">Orientation</label>
                        <Select
                          id="bar-orientation-select"
                          ariaLabel="Orientation"
                          value={spec.style.orientation ?? 'vertical'}
                          onChange={(val) => updateStyle({ orientation: val as never })}
                          options={[
                            { value: 'vertical', label: 'Vertical' },
                            { value: 'horizontal', label: 'Horizontal' },
                          ]}
                        />
                      </div>
                    )}

                    {(spec.type === 'bar' || spec.type === 'area') && (
                      <div className="chartFieldRow">
                        <label htmlFor="chart-stacking-select">Stacking</label>
                        <Select
                          id="chart-stacking-select"
                          ariaLabel="Stacking"
                          value={spec.style.stacking ?? 'none'}
                          onChange={(val) => updateStyle({ stacking: val as never })}
                          options={[
                            { value: 'none', label: 'None' },
                            { value: 'stacked', label: 'Stacked' },
                            { value: 'percent', label: 'Percent (100%)' },
                          ]}
                        />
                      </div>
                    )}

                    {(spec.type === 'line' || spec.type === 'area') && (
                      <>
                        <div className="chartFieldRow">
                          <label htmlFor="curve-select">Curve</label>
                          <Select
                            id="curve-select"
                            ariaLabel="Curve"
                            value={spec.style.curve ?? 'linear'}
                            onChange={(val) => updateStyle({ curve: val as never })}
                            options={[
                              { value: 'linear', label: 'Linear' },
                              { value: 'smooth', label: 'Smooth' },
                              { value: 'step', label: 'Step' },
                            ]}
                          />
                        </div>
                        <div className="chartFieldRow">
                          <label htmlFor="linestyle-select">Line Style</label>
                          <Select
                            id="linestyle-select"
                            ariaLabel="Line Style"
                            value={spec.style.lineStyle ?? 'solid'}
                            onChange={(val) => updateStyle({ lineStyle: val as never })}
                            options={[
                              { value: 'solid', label: 'Solid' },
                              { value: 'dashed', label: 'Dashed' },
                            ]}
                          />
                        </div>
                      </>
                    )}

                    {spec.type === 'burn' && (
                      <div className="chartFieldRow">
                        <label htmlFor="burn-select">Burn Direction</label>
                        <Select
                          id="burn-select"
                          ariaLabel="Burn Direction"
                          value={spec.style.burn ?? 'down'}
                          onChange={(val) => updateStyle({ burn: val as never })}
                          options={[
                            { value: 'down', label: 'Burndown' },
                            { value: 'up', label: 'Burnup' },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Live Preview */}
            <div className="chartBuilderRightCol" data-testid="chart-builder-preview-col">
              <div className="chartPreviewHeader">
                <strong>Preview</strong>
                {isPreviewClientOnly && (
                  <span className="chartPreviewBadge" data-testid="preview-badge">
                    {t('NLSChartsLivePreview')}
                  </span>
                )}
              </div>

              <div className="chartPreviewWrapper" data-testid="chart-builder-preview">
                <ChartEmbed
                  status={previewLoading && !previewData ? 'loading' : !previewData ? 'empty' : 'ready'}
                  testId="chart-builder-preview-embed"
                >
                  {previewData && (
                    <Chart spec={spec} data={previewData} height={300} testId="chart-builder-preview-chart" />
                  )}
                </ChartEmbed>
              </div>
            </div>
          </div>
        </div>
      </AppDialog>

      {/* Unsaved changes confirmation dialog */}
      <AppDialog
        open={confirmDiscardOpen}
        title={t('NLSChartsUnsavedChangesTitle')}
        message={t('NLSChartsUnsavedChangesMessage')}
        onDismiss={() => setConfirmDiscardOpen(false)}
        size="sm"
        actions={[
          {
            label: t('NLSChartsKeepEditing'),
            variant: 'secondary',
            onClick: () => setConfirmDiscardOpen(false),
          },
          {
            label: t('NLSChartsDiscardChanges'),
            variant: 'danger',
            onClick: () => {
              setConfirmDiscardOpen(false)
              setIsDirty(false)
              onClose()
            },
            testId: 'confirm-discard-btn',
          },
        ]}
      />
    </>
  )
}
